/**
 * The SLA matrix — what each kind of ticket promises, and how long it has.
 *
 * **There is no in-place edit, and that is the whole design of this screen.** A ticket pins the
 * policy row it was created under, so editing one would silently move the clocks of every ticket
 * already running against it — and nobody would notice until a breach report looked wrong weeks
 * later. Publishing a new version leaves those tickets on the terms they started under.
 *
 * The screen says so out loud rather than leaving the absence of an edit button to be read as an
 * oversight.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { usePublishSlaVersion, useSlaPolicies } from '../lib/api/hooks/support';
import { SupportReview } from './SupportReview';

const CATEGORIES = [
  'booking_issue',
  'payment_issue',
  'refund_status',
  'pandit_conduct',
  'devotee_conduct',
  'account_access',
  'kyc_onboarding',
  'payout_query',
  'app_technical',
  'content_correction',
  'safety',
  'other',
];

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

/** Minutes are what the server stores; hours are what an operator thinks in. */
export function humanMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 1440 === 0) return `${minutes / 1440} d`;
  if (minutes % 60 === 0) return `${minutes / 60} h`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Why `raw` is not a number of minutes the server will store, or null when it is. */
export function minutesError(raw: string): string | null {
  return /^\s*[1-9]\d*\s*$/.test(raw) ? null : 'Whole minutes, 1 or more';
}

/** A ticket cannot be due resolved before it is due a first reply. */
export function slaOrderError(first: string, resolve: string): string | null {
  if (minutesError(first) || minutesError(resolve)) return null;
  return Number(resolve) < Number(first) ? 'Cannot be sooner than the first reply' : null;
}

export function SlaPolicies() {
  const { scope, toast } = useConsole();
  const policies = useSlaPolicies();
  const publish = usePublishSlaVersion();
  const [category, setCategory] = useState(CATEGORIES[0]!);
  const [priority, setPriority] = useState('normal');
  const [firstEdit, setFirstEdit] = useState<string | null>(null);
  const [resolveEdit, setResolveEdit] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // The form starts from the live version of the pair being edited, so a publish changes only
  // what the operator changed — the escalation role and the pause rule ride along untouched.
  const live = policies.data?.items.find(
    (p) => p.is_active && p.category === category && p.priority === priority,
  );
  const firstResponse = firstEdit ?? String(live?.first_response_minutes ?? 240);
  const resolution = resolveEdit ?? String(live?.resolution_minutes ?? 4320);
  const firstError = minutesError(firstResponse);
  const resolveError = minutesError(resolution) ?? slaOrderError(firstResponse, resolution);
  const unchanged =
    live !== undefined &&
    Number(firstResponse) === live.first_response_minutes &&
    Number(resolution) === live.resolution_minutes;
  const pickPair = (next: () => void) => {
    next();
    setFirstEdit(null);
    setResolveEdit(null);
  };

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  return (
    <>
      <Section
        title="What each ticket promises"
        note="A ticket keeps the version it was opened under, whatever changes later"
      >
        {policies.isPending && <LoadingState message="Reading the matrix…" />}
        {policies.isError && (
          <ErrorState title="Could not read the matrix" cause={errorMessage(policies.error)} />
        )}
        {policies.isSuccess && (
          <DataTable
            density="compact"
            columns={[
              { key: 'category', label: 'Category' },
              { key: 'priority', label: 'Priority' },
              { key: 'version', label: 'Version', align: 'right' },
              { key: 'first', label: 'First reply', align: 'right' },
              { key: 'resolve', label: 'Resolved by', align: 'right' },
              { key: 'state', label: '' },
            ]}
            rows={policies.data.items.map((p) => ({
              id: p.id,
              category: p.category.replace(/_/g, ' '),
              priority: p.priority,
              version: p.version,
              first: humanMinutes(p.first_response_minutes),
              resolve: humanMinutes(p.resolution_minutes),
              state: p.is_active ? (
                <StatusChip tone="success" label="live" />
              ) : (
                <StatusChip tone="neutral" label="superseded" />
              ),
            }))}
          />
        )}
      </Section>

      <Section
        title="Publish a new version"
        note="Editing in place would move the clocks of tickets already running"
      >
        {/* Stated rather than implied. An operator who cannot find an edit button should know
            it is a decision, not an omission. */}
        <p style={{ marginTop: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
          A ticket pins its SLA when it opens. Changing a live policy in place would silently
          re-time every ticket already running against it, and the first anybody would know is a
          breach report that looked wrong. Publishing supersedes the old version and leaves those
          tickets alone.
        </p>
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
          <Select
            label="Category"
            value={category}
            options={CATEGORIES}
            onChange={(v) => pickPair(() => setCategory(v))}
          />
          <Select
            label="Priority"
            value={priority}
            options={PRIORITIES}
            onChange={(v) => pickPair(() => setPriority(v))}
          />
          <Input
            label="First reply (minutes)"
            value={firstResponse}
            onChange={setFirstEdit}
            error={firstError ?? undefined}
          />
          <Input
            label="Resolved by (minutes)"
            value={resolution}
            onChange={setResolveEdit}
            error={resolveError ?? undefined}
            helper={unchanged ? 'The same as the live version — nothing to publish' : undefined}
          />
          <ScopeAction
            scope={scope}
            need="support"
            size="sm"
            disabled={publish.isPending || firstError !== null || resolveError !== null || unchanged}
            onClick={() => setConfirming(true)}
          >
            Publish
          </ScopeAction>
        </div>
      </Section>

      {confirming && (
        <ConfirmAction
          open
          title={`Publish a new SLA for ${category.replace(/_/g, ' ')} / ${priority}?`}
          confirmLabel="Publish"
          phrase="PUBLISH"
          consequences={[
            live
              ? `First reply ${humanMinutes(live.first_response_minutes)} → ${humanMinutes(Number(firstResponse))}; resolved by ${humanMinutes(live.resolution_minutes)} → ${humanMinutes(Number(resolution))}.`
              : `First reply ${humanMinutes(Number(firstResponse))}; resolved by ${humanMinutes(Number(resolution))}. There is no live version of this pair yet.`,
            'Tickets opened from now on get these times.',
            'Tickets already open keep the version they pinned — nothing is re-timed.',
            'The current version is superseded, not deleted; it stays on the list.',
          ]}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            publish.mutate(
              {
                category,
                priority,
                first_response_minutes: Number(firstResponse),
                resolution_minutes: Number(resolution),
                escalation_admin_role: live?.escalation_admin_role ?? 'support',
                pauses_on_pending_customer: live?.pauses_on_pending_customer ?? true,
              },
              {
                onSuccess: () => {
                  setConfirming(false);
                  pickPair(() => undefined);
                  toast({ tone: 'success', message: 'Published' });
                },
                onError: (error) => {
                  setConfirming(false);
                  fail(error);
                },
              },
            )
          }
        />
      )}
      <SupportReview />
    </>
  );
}
